import { createReducer, on } from "@ngrx/store";
import { onLoadableLoad, onLoadableSuccess, onLoadableError, createDefaultLoadable } from '../../models/index';
import { IAlertWindow, initialAlertSelectState, initialAlertState } from "./alert.states";
import { AlertActions, AlertLockActions } from "./alert.actions";
import {
    addCommentToAlert,
    appendActionToAlert,
    getValidAlerts,
    mergeIdentityDetails,
    handleAlertAdd,
    handleAlertUpdate,
    handleDismissAlerts,
    handleEscalateAlerts,
    handleRevertAlerts,
    mergeLocalAlertActions,
    resolveAlertTotal,
    mapSocketLocksToRecord,
    updateWindowWithLocks,
    updateWindowWithoutLocks
} from "./helpers/alert.helpers";
import {
    buildAlertWindows,
    DEFAULT_EMPTY_WINDOW,
    getInitialWindow,
    getWindowAlertIndex,
    moveAlertWindowNew,
    nextAlertInWindow,
    updateWindowSettings
} from "./helpers/window.helpers"
import { excludeKey } from "../common";
import { IAlert } from "../../models/alert.model";
import { UserActions } from "../user/user.actions";
import { createInitialFilter } from "../../../modules/alerts/filter";
import { DateTime } from 'luxon';

export const alertReducer = createReducer(
    initialAlertState,
    on(AlertActions.loadAlerts, (state, { showLoading }) => ({
        ...state,
        alerts: {
            ...(showLoading ? onLoadableLoad(state.alerts) : onLoadableSuccess(state.alerts)),
            items: showLoading ? [] : state.alerts.items,
            loadingPage: showLoading,
            total: showLoading ? 0 : state.alerts.total
        },
        page: showLoading
            ? {
                ...onLoadableLoad(state.page),
                current: 1
            }
            : state.page,
        selectedAlerts: showLoading ? initialAlertSelectState : state.selectedAlerts
    })),
    on(AlertActions.loadAlertsSuccess, (state, { alerts, total, authoritative }) => {
        // Socket alerts can land while the load request is in flight; the server ran its
        // query before they existed, so replacing items with the response outright would
        // silently drop them (and the matching totals make the loss undetectable).
        // Exception: when a similarity/embedding search is active, membership is decided by
        // server-only cosine distance that the client cannot re-evaluate. Retaining items
        // absent from the response would leak stale non-matches (including alerts with no
        // embedding) forward and make the similarity slider appear to have no effect, so the
        // server response is treated as authoritative and nothing is carried over.
        // Aggregate drilldown/leaf loads flag `authoritative` for the same reason: the leaf
        // filter (tag & other group-by dimensions) is not modelled by alertMatchesCurrentFilter,
        // so retained in-flight alerts from a sibling group would inflate the leaf grid and total.
        const embeddingSearchActive: boolean = (state.filter.embedding_searches?.length ?? 0) > 0;
        const serverAuthoritative: boolean = embeddingSearchActive || authoritative === true;
        const alertsWithLocalActionsMerged: IAlert[] = mergeLocalAlertActions(state, alerts);
        const responseAlertIDs = new Set<string>(alertsWithLocalActionsMerged.map(alert => alert.id));
        const inFlightAlerts: IAlert[] = serverAuthoritative
            ? []
            : state.alerts.items.filter(alert => !responseAlertIDs.has(alert.id));
        const filteredAlerts: IAlert[] = getValidAlerts(state, [...alertsWithLocalActionsMerged, ...inFlightAlerts]);
        // The server's total predates those in-flight alerts, so it undercounts them by
        // however many are still retained after filtering.
        const adjustedTotal: number = total + filteredAlerts.filter(alert => responseAlertIDs.has(alert.id) === false).length;
        const finalState = {
            ...state,
            alerts: {
                ...onLoadableSuccess(state.alerts),
                items: filteredAlerts,
                total: adjustedTotal,
                loadingPage: false
            }
        };
        const filter = state.filter;
        const windowSize = state.window.size;
        const windowInterval = state.window.interval;
        const validFilterAlerts: IAlert[] = getValidAlerts(finalState)
        const windows = buildAlertWindows(filteredAlerts, filter, windowSize, windowInterval);

        return {
            ...finalState,
            alertsLoadedAt: Date.now(),
            alerts: {
                ...onLoadableSuccess(finalState.alerts),
                items: validFilterAlerts,
                total: resolveAlertTotal(adjustedTotal, validFilterAlerts, filter),
                loadingPage: false
            },
            window: {
                ...finalState.window,
                windows: windows
            },
            page: {
                ...finalState.page,
                ...createDefaultLoadable()
            }
        }
    }),
    on(AlertActions.loadAlertsFailure, (state, { error }) => ({
        ...state,
        alerts: {
            ...onLoadableError(state.alerts, error),
            loadingPage: false
        },
        page: {
            current: Math.max(1, state.page.current - 1),
            ...createDefaultLoadable()
        }
    })),
    on(AlertActions.setFilterPage, (state, { pageNumber }) => ({
        ...state,
        page: {
            ...state.page,
            current: pageNumber
        }
    })),
    on(AlertActions.clearFilterStore, (state) => ({
        ...state,
        filter: createInitialFilter()
    })),
    on(AlertActions.addAlert, (state, { alert }) => ({
        ...state,
        ...handleAlertAdd(state, [alert])
    })),
    on(AlertActions.incrementAlertTotal, (state) => ({
        ...state,
        alerts: {
            ...state.alerts,
            total: state.alerts.total + 1
        }
    })),
    on(AlertActions.setWindowSettings, (state, { interval, size }) => ({
        ...state,
        window: updateWindowSettings(state, interval, size)
    })),
    on(AlertActions.setWindowAlert, (state, { index }) => {
        let newIndex = index;
        const alertsLength = (state.window.alerts.length - 1);
        const validIndex = (alertsLength >= newIndex) && (newIndex > -1);

        if (!validIndex) {
            newIndex = alertsLength > newIndex ? alertsLength : 0;
        }

        const selectedAlertId = state.window.alerts[newIndex];
        return {
            ...state,
            window: {
                ...state.window,
                currentAlert: newIndex,
                currentAlertId: selectedAlertId
            }
        }
    }),
    on(AlertActions.selectWindowAlert, (state, { index }) => {
        const validIndex = (index <= (state.window.alerts.length - 1)) && (index > -1);
        const selectedAlerts = [...state.window.selected];
        if (validIndex) {
            selectedAlerts.push(state.window.alerts[index])
        }

        return {
            ...state,
            window: {
                ...state.window,
                selected: [...(new Set([...selectedAlerts]).values())]
            }
        }
    }),
    on(AlertActions.deselectWindowAlert, (state, { index }) => ({
        ...state,
        window: {
            ...state.window,
            selected: state.window.selected.filter(e => e !== getWindowAlertIndex(state, index).currentAlertId)
        }
    })),
    on(AlertActions.windowThumbnailSettled, (state, { alertId }) => ({
        ...state,
        window: {
            ...state.window,
            settledThumbnails: settleWindowThumbnail(state.window, alertId)
        }
    })),
    on(AlertActions.nextAlert, state => ({
        ...state,
        window: {
            ...state.window,
            ...nextAlertInWindow(state.window.alerts, state.window.currentAlert)
        }
    })),
    on(AlertActions.moveAlertWindow, (state, { alert }) => ({
        ...state,
        window: moveAlertWindowNew(state, alert),
    })),
    on(AlertActions.loadAlertPage, (state, { page }) => ({
        ...state,
        page: {
            ...onLoadableLoad(state.page),
            current: page
        }
    })),
    on(AlertActions.resetFilterPage, (state) => ({
        ...state,
        page: {
            current: 1,
            ...createDefaultLoadable()
        }
    })),
    on(AlertActions.loadAlertPageSuccess, (state, { alerts, newTotal, newPage }) => {
        const alertsWithLocalActionsMerged: IAlert[] = mergeLocalAlertActions(state, alerts);
        const updatedAlerts = [...state.alerts.items, ...alertsWithLocalActionsMerged].reduceRight<IAlert[]>((acc, alert) => {
            if (!acc.some(e => e.id === alert.id)) {
                acc.unshift(alert);
            }
            return acc;
        }, []);

        return {
            ...state,
            alerts: {
                ...state.alerts,
                items: updatedAlerts,
                total: resolveAlertTotal(newTotal, updatedAlerts, state.filter)
            },
            window: {
                ...state.window.alerts.length === 0 ? getInitialWindow(state, [...state.alerts.items, ...alertsWithLocalActionsMerged]) : state.window,
                windows: buildAlertWindows(updatedAlerts, state.filter, state.window.size, state.window.interval)
            },
            page: {
                ...onLoadableSuccess(state.page),
                current: newPage
            }
        }
    }),
    on(AlertActions.loadAlertPageFailure, (state, { error }) => ({
        ...state,
        page: {
            ...onLoadableError(state.page, error),
        }
    })),
    on(AlertActions.updateAlerts, (state, { alerts }) => ({
        ...state,
        ...alerts.reduce((currentState, alert) => {
            return {
                ...currentState,
                ...handleAlertUpdate(currentState, alert)
            };
        }, state)
    })),
    on(AlertActions.appendAlertAction, (state, { alertId, action }) => {
        const alertExists = state.alerts.items.some(a => a.id === alertId);
        if (!alertExists) {
          return state;
        }
        return {
            ...state,
            alerts: {
                ...state.alerts,
                items: state.alerts.items.map(a => appendActionToAlert(a, alertId, action))
            },
            window: {
                ...state.window,
                fullAlerts: state.window.fullAlerts.map(a => appendActionToAlert(a, alertId, action))
            }
        };
    }),
    on(AlertActions._dismissAlerts, (state, { alerts, userID }) => {
        return {
            ...state,
            ...handleDismissAlerts(state, alerts, userID)
        }
    }),
    on(AlertActions.dismissAlertsSuccess, (state, { alerts, userID }) => {
        return {
            ...state,
            ...handleDismissAlerts(state, alerts, userID)
        }
    }),
    on(AlertActions.dismissAlertsFailure, (state, { alerts }) => ({
        ...state,
        alerts: handleRevertAlerts(state, alerts)
    })),
    on(AlertActions._escalateAlerts, (state, { alerts, userID }) => ({
        ...state,
        ...handleEscalateAlerts(state, alerts, userID)
    })),
    on(AlertActions.escalateAlertsSuccess, (state, { alerts, userID }) => ({
        ...state,
        ...handleEscalateAlerts(state, alerts, userID)
    })),
    on(AlertActions.escalateAlertsFailure, (state, { alerts }) => ({
        ...state,
        alerts: handleRevertAlerts(state, alerts)
    })),
    on(AlertLockActions.acquireNewLocks, (state) => ({
        ...state,
        window: {
            ...state.window,
            requestingNextLock: true
        }
    })),
    on(AlertLockActions.releaseLocksSuccess, (state, { ids }) => ({
        ...state,
        locks: excludeKey(state.locks, ...ids),
        window: {
            ...state.window,
            alertIdsToRelease: state.window.alertIdsToRelease.filter((alertId) => !ids.includes(alertId))
        }
    })),
    on(AlertLockActions.setLocks, (state, { locks, userId }) => {
        // The operator keeps the group they are working on until they action it. A reply to a
        // duplicate lock request grants the next group instead; releaseUnusedGrantedLocks$ frees it.
        const userIsWorkingOnWindow: boolean = state.window.alerts.some((alertId) => state.locks[alertId]?.userID === userId);
        return {
            ...state,
            window: {
                ...(userIsWorkingOnWindow ? state.window : updateWindowWithLocks(state.window, locks, state.alerts.items, userId, state.filter)),
                alertIdsToRelease: [...new Set([...state.window.alertIdsToRelease, ...locks.map((alertLock) => alertLock.alertID)])],
                requestingNextLock: false
            },
            locks: {
                ...state.locks,
                ...mapSocketLocksToRecord(locks)
            }
        };
    }),
    on(AlertLockActions.setWindowWithoutConnection, (state) => {
        const nextWindow = updateWindowWithoutLocks(state.window, state.alerts.items, state.filter);
        if (state.window.alerts.length === 0 && nextWindow.alerts.length === 0 && !state.window.requestingNextLock) {
            return state;
        }
        return {
            ...state,
            window: {
                ...nextWindow,
                requestingNextLock: false
            },
        };
    }),
    on(AlertLockActions.setOtherLocks, (state, { locks, userId }) => {
        // The reply only covers alerts still in window.windows, so a lock on a viewed alert that has
        // since left the filter (e.g. dismissed by Scout) would be lost and setLocks could swap the group.
        const ownWindowLocks: typeof state.locks = {};
        for (const alertId of state.window.alerts) {
            if (state.locks[alertId]?.userID === userId) {
                ownWindowLocks[alertId] = state.locks[alertId];
            }
        }
        return {
            ...state,
            locks: {
                ...ownWindowLocks,
                ...mapSocketLocksToRecord(locks)
            }
        };
    }),
    on(AlertActions.setSocketConnected, (state, { connected }) => ({
        ...state,
        socketConnected: connected
    })),
    on(AlertActions.setAlertsPageActive, (state, { active }) => ({
        ...state,
        isAlertsPageActive: active
    })),
    on(AlertLockActions.toggleCanAcquireLocks, (state, { acquireLocks }) => ({
        ...state,
        window: {
            ...state.window,
            canAcquireLocks: acquireLocks
        }
    })),
    on(AlertLockActions.toggleRequestingNextLock, (state, { requestLock }) => ({
        ...state,
        window: {
            ...state.window,
            requestingNextLock: requestLock
        }
    })),
    on(AlertLockActions.setUserId, (state, { userId }) => ({
        ...state,
        userId: userId
    })),
    on(AlertActions.buildAlertWindows, (state) => ({
        ...state,
        window: {
            ...state.window,
            windows: buildAlertWindows(state.alerts.items, state.filter, state.window.size, state.window.interval),
        }
    })),
    on(AlertActions.resetWindowState, (state) => ({
        ...state,
        window: {
            ...state.window,
            alerts: [],
            fullAlerts: [],
            selected: [],
            // windows: buildAlertWindows(state.alerts.items, state.filter, state.window.size, state.window.interval),
            windows: [],
            currentAlertId: undefined,
            currentAlert: 0,
            currentWindowIndex: 0
        }
    })),
    on(AlertActions.addComment, (state, { comment }) => ({
        ...state,
        alerts: {
            ...state.alerts,
            items: state.alerts.items.map(e => addCommentToAlert({ ...e }, comment))
        },
        window: {
            ...state.window,
            fullAlerts: state.window.fullAlerts.map(e => addCommentToAlert({ ...e }, comment))
        }
    })),
    on(AlertActions.commentOnAlertsSuccess, (state, { comments }) => ({
        ...state,
        alerts: {
            ...state.alerts,
            items: state.alerts.items.map(e => ({
                ...e,
                comments: [...e.comments, ...comments.filter(c => c.alert_id === e.id && !e.comments.map((ec) => ec.id).includes(c.id))]
            }))
        }
    }
    )),
    on(AlertActions.setAlertViewTimestamp, (state, { alertIds }) => ({
        ...state,
        viewTimestamps: {
            ...state.viewTimestamps,
            [alertIds[0]]: DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss')
        }
    })),
    on(AlertActions.clearAlertViewTimestamps, (state, { alertIds }) => ({
        ...state,
        viewTimestamps: excludeKey(state.viewTimestamps, ...alertIds)
    })),
    on(AlertActions.clearAllAlertviewTimestamp, (state) => ({
        ...state,
        viewTimestamps: {}
    })),
    on(AlertActions.setKeyboardMode, (state, { enabled }) => ({
        ...state,
        keyboardMode: enabled
    })),
    on(AlertActions.setAlertVideoAutoplay, (state, { enabled }) => ({
        ...state,
        alertVideoAutoplay: enabled
    })),
    on(AlertActions.setAlertVideoOverlays, (state, { enabled }) => ({
        ...state,
        alertVideoOverlays: enabled
    })),
    on(AlertActions.setDismissOnMute, (state, { enabled }) => ({
        ...state,
        dismissOnMute: enabled
    })),
    on(AlertActions.setFilter, (state, { filter }) => ({
        ...state,
        filter: { ...filter },
        window: {
            ...state.window,
            ...DEFAULT_EMPTY_WINDOW
        }
    })),
    on(AlertActions.addEmbeddingSearch, (state, { alertId, detectionId, similarity, detectionLabel, cameraLabel, timeLabel, imageUrl, boundingBox, negative }) => {
        const existing = state.filter.embedding_searches ?? [];
        const without = existing.filter(s => s.detectionId !== detectionId);
        return {
            ...state,
            filter: {
                ...state.filter,
                embedding_searches: [...without, { alertId, detectionId, similarity, detectionLabel, cameraLabel, timeLabel, imageUrl, boundingBox, negative }],
            },
        };
    }),
    on(AlertActions.hideAlert, (state, { alertId }) => ({
        ...state,
        hiddenAlertIds: state.hiddenAlertIds.includes(alertId)
            ? state.hiddenAlertIds
            : [...state.hiddenAlertIds, alertId],
    })),
    on(AlertActions.clearHiddenAlerts, (state) => ({
        ...state,
        hiddenAlertIds: [],
    })),
    on(AlertActions.removeEmbeddingSearch, (state, { detectionId }) => ({
        ...state,
        filter: {
            ...state.filter,
            embedding_searches: (state.filter.embedding_searches ?? []).filter(s => s.detectionId !== detectionId),
        },
    })),
    // Re-staging the same detection with the same polarity toggles it off; the opposite
    // polarity flips it. The filter itself is untouched until applyStagedEmbeddingSearches.
    on(AlertActions.stageEmbeddingSearch, (state, { alertId, detectionId, similarity, detectionLabel, cameraLabel, timeLabel, imageUrl, boundingBox, negative }) => {
        const existing = state.stagedEmbeddingSearches.find(s => s.detectionId === detectionId);
        const without = state.stagedEmbeddingSearches.filter(s => s.detectionId !== detectionId);
        if (existing != null && (existing.negative === true) === (negative === true)) {
            return { ...state, stagedEmbeddingSearches: without };
        }
        return {
            ...state,
            stagedEmbeddingSearches: [...without, { alertId, detectionId, similarity, detectionLabel, cameraLabel, timeLabel, imageUrl, boundingBox, negative }],
        };
    }),
    on(AlertActions.unstageEmbeddingSearch, (state, { detectionId }) => ({
        ...state,
        stagedEmbeddingSearches: state.stagedEmbeddingSearches.filter(s => s.detectionId !== detectionId),
    })),
    on(AlertActions.clearStagedEmbeddingSearches, (state) => ({
        ...state,
        stagedEmbeddingSearches: [],
    })),
    on(AlertActions.updateEmbeddingSearchSimilarity, (state, { detectionId, similarity }) => ({
        ...state,
        filter: {
            ...state.filter,
            embedding_searches: (state.filter.embedding_searches ?? []).map(s =>
                s.detectionId === detectionId ? { ...s, similarity } : s
            ),
        },
    })),
    on(AlertActions.setPausedState, (state, { newMode }) => ({
        ...state,
        paused: {
            paused: newMode
        },
        window: {
            ...state.window,
            ...DEFAULT_EMPTY_WINDOW,
            ...(!newMode ? {
                windows: buildAlertWindows(state.alerts.items, state.filter, state.window.size, state.window.interval),
            } : {
                windows: []
            }),
        },
        viewTimestamps: {}
    })),
    on(AlertActions.selectAlert, (state, { alert }) => ({
        ...state,
        selectedAlerts: {
            ...state.selectedAlerts,
            selected: state.selectedAlerts.selected.add(alert.id),
            deselected: new Set([...state.selectedAlerts.deselected.values()].filter(e => e !== alert.id)),
            allSelected: state.alerts.total === state.selectedAlerts.currentCount + 1,
            currentCount: state.selectedAlerts.currentCount + 1,
        }
    })),
    on(AlertActions.deselectAlert, (state, { alert }) => ({
        ...state,
        selectedAlerts: {
            ...state.selectedAlerts,
            selected: new Set([...state.selectedAlerts.selected.values()].filter(e => e !== alert.id)),
            deselected: state.selectedAlerts.deselected.add(alert.id),
            allSelected: false,
            currentCount: state.selectedAlerts.currentCount - 1
        }
    })),
    on(AlertActions.clearSelectedAlerts, state => ({
        ...state,
        selectedAlerts: {
            ...createDefaultLoadable(),
            selected: new Set<string>(),
            deselected: new Set<string>(),
            allSelected: false,
            currentCount: 0
        }
    })),
    // Hidden alerts are excluded: they stay deselected so bulk actions skip them server-side.
    on(AlertActions.selectAllAlerts, state => ({
        ...state,
        selectedAlerts: {
            ...createDefaultLoadable(),
            selected: new Set(getValidAlerts(state).map(e => e.id).filter(id => !state.hiddenAlertIds.includes(id))),
            deselected: new Set<string>(state.hiddenAlertIds),
            allSelected: true,
            currentCount: state.alerts.total - state.hiddenAlertIds.length
        }
    })),
    // Todo: Update current alerts on success
    on(AlertActions.dismissFilter, state => ({
        ...state,
        selectedAlerts: {
            ...onLoadableLoad(state.selectedAlerts),
        }
    })),
    on(AlertActions.dismissFilterSuccess, state => ({
        ...state,
        selectedAlerts: {
            ...onLoadableSuccess(state.selectedAlerts),
            selected: new Set<string>(),
            deselected: new Set<string>(),
            allSelected: false,
            currentCount: 0,
        }
    })),
    on(AlertActions.dismissFilterFailure, (state, { error }) => ({
        ...state,
        selectedAlerts: {
            ...onLoadableError(state.selectedAlerts, error),
        }
    })),

    on(AlertActions.escalateFilter, state => ({
        ...state,
        selectedAlerts: {
            ...onLoadableLoad(state.selectedAlerts),
        }
    })),
    on(AlertActions.escalateFilterSuccess, state => ({
        ...state,
        selectedAlerts: {
            ...onLoadableSuccess(state.selectedAlerts),
            selected: new Set<string>(),
            deselected: new Set<string>(),
            allSelected: false,
            currentCount: 0,
        }
    })),
    on(AlertActions.escalateFilterFailure, (state, { error }) => ({
        ...state,
        selectedAlerts: {
            ...onLoadableError(state.selectedAlerts, error),
        }
    })),
    on(AlertActions.scoutReviewFilter, state => ({
        ...state,
        selectedAlerts: onLoadableLoad(state.selectedAlerts)
    })),
    on(AlertActions.scoutReviewFilterSuccess, state => ({
        ...state,
        selectedAlerts: {
            ...onLoadableSuccess(state.selectedAlerts),
            selected: new Set<string>(),
            deselected: new Set<string>(),
            allSelected: false,
            currentCount: 0,
        }
    })),
    on(AlertActions.scoutReviewFilterFailure, (state, { error }) => ({
        ...state,
        selectedAlerts: onLoadableError(state.selectedAlerts, error)
    })),

    on(AlertActions.setPauseAfterAlertActionSuccess, (state, { enabled }) => ({
        ...state,
        pauseAfterAlertAction: enabled
    })),
    on(AlertActions.loadFocusModeSettingsSuccess, (state, { pauseAfterAlertAction }) => ({
        ...state,
        pauseAfterAlertAction: pauseAfterAlertAction,
    })),
    on(AlertActions.resolveIdentityDetails, (state, { alertId, detectionId }) => ({
        ...state,
        identityDetailsLoading: {
            ...state.identityDetailsLoading,
            [`${alertId}:${detectionId}`]: true,
        },
    })),
    on(AlertActions.resolveIdentityDetailsFailure, (state, { alertId, detectionId }) => ({
        ...state,
        identityDetailsLoading: excludeKey(state.identityDetailsLoading, `${alertId}:${detectionId}`),
    })),
    on(AlertActions.resolveIdentityDetailsSuccess, (state, { alertId, detectionId, identityDetails }) => {
        const identityDetailsLoading = excludeKey(state.identityDetailsLoading, `${alertId}:${detectionId}`);
        return {
            ...state,
            identityDetailsLoading,
            alerts: {
                ...state.alerts,
                items: state.alerts.items.map(a => a.id === alertId ? mergeIdentityDetails(a, detectionId, identityDetails) ?? a : a),
            },
            window: {
                ...state.window,
                fullAlerts: state.window.fullAlerts.map(a => a.id === alertId ? mergeIdentityDetails(a, detectionId, identityDetails) ?? a : a),
            },
        };
    }),

    on(UserActions.logout, () => ({
        ...initialAlertState
    }))
)

// Keeps only ids still in the window, so the list can't grow across groups.
function settleWindowThumbnail(window: IAlertWindow, alertId: string): string[] {
    const settled: string[] = window.settledThumbnails.filter(id => id !== alertId && window.alerts.includes(id));
    return [...settled, alertId];
}
